/**
 * The gate.
 *
 * Everything behind it assumes a signed-in caller, so this renders the sign-in page
 * instead of the app rather than redirecting — the URL is preserved, and someone who
 * was timed out mid-task lands back where they were once they sign in again.
 *
 * This is a convenience, not the security boundary. Every route is enforced again at
 * the API, which is the only place enforcement counts: hiding a page hides a button,
 * it does not stop a request.
 */

import { useEffect, type ReactNode } from 'react';

import { setUnauthorizedHandler } from '@/lib/api';

import { useAuth } from './AuthProvider';
import { IdleWarning } from './IdleWarning';
import { SignInPage } from './SignIn';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, signOut } = useAuth();

  /*
   * If the API rejects a token, end the session rather than letting each panel render
   * its own error. A revoked or expired refresh token is indistinguishable from a
   * sign-out from the app's point of view, and should look the same to the user.
   */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      void signOut(null);
    });
    return () => setUnauthorizedHandler(null);
  }, [signOut]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        {/* Deliberately wordless: this is visible for a few hundred milliseconds while
            the stored session is checked, and a sentence that flashes is worse than
            nothing. */}
        <div
          className="h-6 w-6 rounded-full border-2 border-border border-t-[var(--color-primary)] animate-spin"
          role="status"
          aria-label="…"
        />
      </div>
    );
  }

  if (status === 'signedOut') return <SignInPage />;

  return (
    <>
      {children}
      <IdleWarning />
    </>
  );
}
